




























import { Component, Prop, Vue } from 'vue-property-decorator';

type Place = 'top' | 'bottom' | 'left' | 'right';

@Component
export default class InfoTip extends Vue {
  /* Props */
  @Prop({ type: String, required: true }) readonly text!: string;
  @Prop({ type: String, default: 'top' }) readonly placement!: Place;
  @Prop({ type: [String, Number], default: 220 }) readonly minWidth!: string | number;
  @Prop({ type: [String, Number], default: 300 }) readonly maxWidth!: string | number; // largeur max par défaut
  @Prop({ type: Number, default: 8 }) readonly gutter!: number; // marge au bord de l'écran
  @Prop({ type: Boolean, default: false }) readonly debug!: boolean; // toujours visible (debug)

  /* État (initialisé => réactif) */
  show = false; // monte/démonte le DOM
  visible = false; // visibilité (anti-flash)
  effective: Place = 'top';
  pos = { left: 0, top: 0 };
  arrow = { x: 0, y: 0 };

  created() {
    this.effective = this.placement as Place;
  }

  beforeDestroy() {
    this.detachWin();
  }

  private toPx(v: string | number) {
    return typeof v === 'number' ? `${v}px` : v;
  }

  get bubbleInlineStyle() {
    return {
      left: `${this.pos.left}px`,
      top: `${this.pos.top}px`,
      minWidth: this.toPx(this.minWidth),
      maxWidth: this.toPx(this.maxWidth),
      '--arrow-x': `${this.arrow.x}px`,
      '--arrow-y': `${this.arrow.y}px`,
    } as any;
  }

  open() {
    if (this.debug) return;
    if (this.show) return;

    this.show = true;
    this.visible = false; // cachée tant que non positionnée

    this.$nextTick(() => {
      // on laisse le layout se stabiliser avant mesure
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.reposition(true);
          this.attachWin();
          // afficher seulement une fois positionnée
          requestAnimationFrame(() => {
            this.visible = true;
          });
        });
      });
    });
  }

  close() {
    if (this.debug) return;
    if (!this.show) return;

    this.visible = false; // petit fade-out
    setTimeout(() => {
      this.show = false;
      this.detachWin();
    }, 130); // démonte après l’anim
  }

  /* Listeners fenêtre */
  private onWin = () => {
    if (this.debug || this.show) this.$nextTick(() => this.reposition(false));
  };
  private attachWin() {
    window.addEventListener('scroll', this.onWin, true);
    window.addEventListener('resize', this.onWin);
  }
  private detachWin() {
    window.removeEventListener('scroll', this.onWin, true);
    window.removeEventListener('resize', this.onWin);
  }

  /* Utilitaires sûrs */
  private safeRect(el?: Element | null): DOMRect | null {
    try {
      return el ? el.getBoundingClientRect() : null;
    } catch {
      return null;
    }
  }

  /* Positionnement + auto-flip + clamp */
  private reposition(firstTry: boolean) {
    const wrap = this.$refs.wrapper as HTMLElement | undefined;
    const bubble = this.$refs.bubble as HTMLElement | undefined;
    if (!wrap || !bubble) return;

    // rendre mesurable sans clignoter
    const prevVis = bubble.style.visibility;
    const prevDisp = bubble.style.display;
    bubble.style.visibility = 'hidden';
    bubble.style.display = 'inline-block';

    const vw = window.innerWidth,
      vh = window.innerHeight;
    const wRect = this.safeRect(wrap);
    const bRect0 = this.safeRect(bubble);

    if (!wRect || !bRect0) {
      // DOM pas prêt → retente une fois
      bubble.style.visibility = prevVis;
      bubble.style.display = prevDisp;
      if (firstTry) requestAnimationFrame(() => this.reposition(false));
      return;
    }

    const bw = bRect0.width || 0,
      bh = bRect0.height || 0;
    const anchorX = wRect.left + wRect.width / 2;
    const anchorY = wRect.top + wRect.height / 2;
    const g = this.gutter;

    const compute = (place: Place): { left: number; top: number } => {
      switch (place) {
        case 'top':
          return { left: anchorX - bw / 2, top: wRect.top - bh - g };
        case 'bottom':
          return { left: anchorX - bw / 2, top: wRect.bottom + g };
        case 'left':
          return { left: wRect.left - bw - g, top: anchorY - bh / 2 };
        case 'right':
          return { left: wRect.right + g, top: anchorY - bh / 2 };
        default:
          return { left: anchorX - bw / 2, top: wRect.bottom + g };
      }
    };

    // position préférée
    let place: Place = (this.placement || 'top') as Place;
    let p = compute(place);

    // débordements
    const overTop = p.top < g;
    const overBottom = p.top + bh > vh - g;
    const overLeft = p.left < g;
    const overRight = p.left + bw > vw - g;

    // auto-flip
    if (place === 'top' && overTop) {
      place = 'bottom';
      p = compute(place);
    }
    if (place === 'bottom' && overBottom) {
      place = 'top';
      p = compute(place);
    }
    if (place === 'left' && overLeft) {
      place = 'right';
      p = compute(place);
    }
    if (place === 'right' && overRight) {
      place = 'left';
      p = compute(place);
    }

    // clamp au viewport
    const left = Math.min(Math.max(p.left, g), Math.max(vw - bw - g, g));
    const top = Math.min(Math.max(p.top, g), Math.max(vh - bh - g, g));

    // flèche centrée (clampée à l’intérieur de la bulle)
    const arrowX = Math.min(Math.max(anchorX - left, 6), Math.max(bw - 6, 6));
    const arrowY = Math.min(Math.max(anchorY - top, 6), Math.max(bh - 6, 6));

    this.effective = place;
    this.pos = { left, top };
    this.arrow = { x: arrowX, y: arrowY };

    // restaurer style "measurement"
    bubble.style.visibility = prevVis;
    bubble.style.display = prevDisp || 'inline-block';
  }
}
